/** Mirrors the DTOs the My Tracker API returns. */

export interface User {
  id: string
  email: string
  displayName: string
  createdAt?: string
  /** Profile details live on the account, so they are the same on every device. All optional. */
  heightCm?: number
  weightKg?: number
  dateOfBirth?: string
  /** Worked out from dateOfBirth by the server, so it never goes stale. Read-only. */
  age?: number
  gender?: Gender
  /** ISO 3166-1 alpha-2 code ("NZ"); see countries.ts for turning it back into a name. */
  country?: string
}

export type Gender = "FEMALE" | "MALE" | "OTHER" | "PREFER_NOT_TO_SAY"

export const GENDERS: { value: Gender; label: string }[] = [
  { value: "FEMALE", label: "Female" },
  { value: "MALE", label: "Male" },
  { value: "OTHER", label: "Other" },
  { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
]

/**
 * What an activity actually was. Richer than the TCX sport, which the format restricts to
 * Running, Biking and Other. The API sends and accepts these names.
 */
export type ExerciseType = "RUNNING" | "WALKING" | "HIKING" | "CYCLING" | "OTHER"

export const EXERCISE_TYPES: { value: ExerciseType; label: string }[] = [
  { value: "RUNNING", label: "Running" },
  { value: "WALKING", label: "Walking" },
  { value: "HIKING", label: "Hiking" },
  { value: "CYCLING", label: "Cycling" },
  { value: "OTHER", label: "Other" },
]

export function exerciseTypeLabel(value: string): string {
  return EXERCISE_TYPES.find((t) => t.value === value)?.label ?? "Other"
}

export interface AuthResponse {
  token: string
  expiresAt: string
  user: User
}

export interface ActivitySummary {
  id: string
  tcxActivityId: string
  /** The TCX sport: only ever Running, Biking or Other. */
  sport: string
  exerciseType: ExerciseType
  startTime: string
  endTime?: string
  totalTimeSeconds: number
  distanceMeters: number
  avgSpeedMps?: number
  maxSpeedMps?: number
  avgPaceSecondsPerKm?: number
  calories?: number
  avgHeartRateBpm?: number
  maxHeartRateBpm?: number
  avgCadence?: number
  maxCadence?: number
  totalAscentMeters?: number
  totalDescentMeters?: number
  notes?: string
  creatorName?: string
  uploadedAt: string
  lapCount: number
}

export interface Lap {
  id: string
  lapIndex: number
  startTime: string
  totalTimeSeconds: number
  distanceMeters: number
  maximumSpeedMps?: number
  calories?: number
  averageHeartRateBpm?: number
  maximumHeartRateBpm?: number
  intensity?: string
  cadence?: number
  triggerMethod?: string
  notes?: string
  avgSpeedMps?: number
  maxBikeCadence?: number
  avgRunCadence?: number
  maxRunCadence?: number
  steps?: number
  avgWatts?: number
  maxWatts?: number
  avgPaceSecondsPerKm?: number
}

export interface Trackpoint {
  seq: number
  time: string
  lat?: number
  lon?: number
  altitudeMeters?: number
  distanceMeters?: number
  heartRateBpm?: number
  cadence?: number
  sensorState?: string
  speedMps?: number
  runCadence?: number
  watts?: number
}

/** Just enough of a neighbouring activity to label and link a paging button. */
export interface Neighbour {
  id: string
  startTime: string
  exerciseType: ExerciseType
}

export interface ActivityDetail {
  summary: ActivitySummary
  laps: Lap[]
  creatorUnitId?: string
  creatorProductId?: string
  creatorVersion?: string
  authorName?: string
  authorBuild?: string
  trackpointCount: number
  /** Absent at the most recent activity. */
  newer?: Neighbour
  /** Absent at the oldest. */
  older?: Neighbour
}

export interface StatsSummary {
  days: number
  runs: number
  distanceMeters: number
  movingSeconds: number
  calories: number
  avgHeartRateBpm?: number
  maxHeartRateBpm?: number
  avgPaceSecondsPerKm?: number
}

/** Spring returns a page envelope for the activity list. */
export interface Page<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}
